import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from './AdminLayout';
import axios from 'axios';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { Eye, Users, Activity, Star, ChevronLeft, ChevronRight, RefreshCw, ArrowUp, ArrowDown, UserCheck, UserX } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const PERIODS = [
  { value: 'today', label: 'Dziś' },
  { value: 'yesterday', label: 'Wczoraj' },
  { value: '7d', label: '7 dni' },
  { value: '30d', label: '30 dni' },
  { value: '1y', label: 'Rok' },
  { value: 'all', label: 'Wszystko' },
];

const METRICS = {
  views: { label: 'Wszystkie wizyty', short: 'Wizyty', color: '#10b981' },
  visitors: { label: 'Unikalne', short: 'Unikalne', color: '#6366f1' },
};

// "Today" in Warsaw (site is PL) — en-CA gives YYYY-MM-DD; avoids UTC off-by-one after midnight
const todayStr = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Europe/Warsaw' });
const shiftDay = (ds, delta) => {
  const d = new Date(ds + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() + delta);
  return d.toISOString().slice(0, 10);
};
const fmtDay = (ds) => {
  if (!ds) return '—';
  const d = new Date(ds + 'T00:00:00Z');
  return d.toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC' });
};
const nf = (n) => (n ?? 0).toLocaleString('pl-PL');

const StatCard = ({ label, value, sub, color = '#10b981', icon: Icon }) => (
  <div className="glass-card p-5 flex items-center gap-4">
    {Icon && (
      <div style={{ background: color + '20', borderRadius: 12, padding: 10 }}>
        <Icon size={22} color={color} />
      </div>
    )}
    <div>
      <p className="text-[#6B7280] text-xs mb-1">{label}</p>
      <p className="text-white text-2xl font-bold">{value}</p>
      {sub && <div className="text-xs mt-0.5">{sub}</div>}
    </div>
  </div>
);

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const p = payload[0]?.payload || {};
  return (
    <div style={{ background: '#111827', border: '1px solid #1f2937', borderRadius: 8, padding: '10px 14px' }}>
      <p style={{ color: '#9CA3AF', fontSize: 12, marginBottom: 4 }}>{fmtDay(label)}</p>
      <p style={{ color: '#10b981', fontSize: 14, fontWeight: 700 }}>{nf(p.views)} wizyt</p>
      <p style={{ color: '#6366f1', fontSize: 12 }}>{nf(p.visitors)} unikalnych</p>
    </div>
  );
};

const AdminVisits = () => {
  const [period, setPeriod] = useState('30d');
  const [metric, setMetric] = useState('views');      // 'views' (all) | 'visitors' (unique)
  const [excludeMe, setExcludeMe] = useState(true);   // true = "Bez moich", false = "Z moimi"
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Day stepper
  const [selectedDay, setSelectedDay] = useState(todayStr());
  const [dayData, setDayData] = useState(null);

  // Month/year calendar (past & future) → explicit date range
  const [rangeStart, setRangeStart] = useState('');
  const [rangeEnd, setRangeEnd] = useState('');
  const [selMonth, setSelMonth] = useState('');
  const [selYear, setSelYear] = useState('');

  // "This is my browser" — visits from here are tagged owner, so they can be subtracted
  const [isOwner, setIsOwner] = useState(
    localStorage.getItem('mm_owner') === '1' || localStorage.getItem('mm_ignore_visits') === '1'
  );
  const toggleOwner = () => {
    const next = !isOwner;
    if (next) {
      localStorage.setItem('mm_owner', '1');
    } else {
      localStorage.removeItem('mm_owner');
      localStorage.removeItem('mm_ignore_visits');
    }
    setIsOwner(next);
  };

  const token = sessionStorage.getItem('adminToken');
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const q = (rangeStart && rangeEnd) ? `start=${rangeStart}&end=${rangeEnd}` : `period=${period}`;
      const res = await axios.get(`${API}/admin/analytics/visits?${q}`, { headers });
      setData(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [period, rangeStart, rangeEnd]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchDay = useCallback(async (day) => {
    try {
      const res = await axios.get(`${API}/admin/analytics/visits/day?date=${day}`, { headers });
      setDayData(res.data);
    } catch (e) {
      console.error(e);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { fetchDay(selectedDay); }, [selectedDay, fetchDay]);

  const MONTHS_PL = ['styczeń', 'luty', 'marzec', 'kwiecień', 'maj', 'czerwiec', 'lipiec', 'sierpień', 'wrzesień', 'październik', 'listopad', 'grudzień'];
  // Year list: past + future (2025 … current+2), so future months are selectable ahead of time
  const yearOptions = React.useMemo(() => {
    const y = new Date().getFullYear();
    const arr = [];
    for (let yr = y + 2; yr >= 2025; yr--) arr.push(yr);
    return arr;
  }, []);
  const applyYM = (m, y) => {
    setSelMonth(m); setSelYear(y);
    if (!m || !y) { setRangeStart(''); setRangeEnd(''); return; }
    const mm = String(m).padStart(2, '0');
    const last = new Date(Number(y), Number(m), 0).getDate();   // last day of month m (1-based)
    setRangeStart(`${y}-${mm}-01`);
    setRangeEnd(`${y}-${mm}-${String(last).padStart(2, '0')}`);
  };
  const clearRange = () => { setRangeStart(''); setRangeEnd(''); setSelMonth(''); setSelYear(''); };
  const rangeActive = Boolean(rangeStart && rangeEnd);
  const pickPeriod = (v) => { setPeriod(v); clearRange(); };

  const M = METRICS[metric];
  const other = metric === 'views' ? 'visitors' : 'views';
  const isToday = selectedDay === todayStr();

  // Subtract owner (my) visits when "Bez moich" is selected
  const sub = (val, ownerVal) => Math.max(0, (val || 0) - (excludeMe ? (ownerVal || 0) : 0));

  // Metric-driven derived values
  let total = 0, otherTotal = 0, avg = 0, todayVal = 0, todayOther = 0, change = null;
  let best = { date: null, value: 0 };
  let chartData = [];
  if (data) {
    total = sub(data.totals[metric], data.totals['owner_' + metric]);
    otherTotal = sub(data.totals[other], data.totals['owner_' + other]);
    avg = data.totals.days ? Math.round((total / data.totals.days) * 10) / 10 : 0;
    todayVal = sub(data.today?.[metric], data.today?.['owner_' + metric]);
    todayOther = sub(data.today?.[other], data.today?.['owner_' + other]);
    const prevVal = sub(data.previous?.[metric], data.previous?.['owner_' + metric]);
    if (prevVal > 0) change = Math.round(((total - prevVal) / prevVal) * 1000) / 10;
    else if (total > 0) change = 100;

    chartData = data.series.map(s => {
      const v = sub(s.views, s.owner_views);
      const u = sub(s.visitors, s.owner_visitors);
      const val = metric === 'views' ? v : u;
      if (val > best.value) best = { date: s.date, value: val };
      return { date: s.date, value: val, views: v, visitors: u };
    });
  }

  const dayVal = sub(dayData?.[metric], dayData?.['owner_' + metric]);
  const dayOther = sub(dayData?.[other], dayData?.['owner_' + other]);

  return (
    <AdminLayout title="Odwiedziny">
      {/* Toggles row */}
      <div className="flex items-center gap-3 mb-2 flex-wrap">
        {/* Metric: all vs unique */}
        <div className="inline-flex rounded-xl bg-[#0B0F0E] border border-white/5 p-1">
          {Object.entries(METRICS).map(([key, m]) => (
            <button key={key} onClick={() => setMetric(key)}
              className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all ${metric === key ? 'text-white' : 'text-[#9CA3AF] hover:text-white'}`}
              style={metric === key ? { background: m.color } : {}}>
              {m.label}
            </button>
          ))}
        </div>

        {/* Display: with / without my visits */}
        <div className="inline-flex rounded-xl bg-[#0B0F0E] border border-white/5 p-1">
          <button onClick={() => setExcludeMe(true)}
            className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all ${excludeMe ? 'bg-[#374151] text-white' : 'text-[#9CA3AF] hover:text-white'}`}>
            Bez moich
          </button>
          <button onClick={() => setExcludeMe(false)}
            className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all ${!excludeMe ? 'bg-[#374151] text-white' : 'text-[#9CA3AF] hover:text-white'}`}>
            Z moimi
          </button>
        </div>

        {/* This is my browser */}
        <button onClick={toggleOwner}
          title="Oznacz tę przeglądarkę jako swoją — Twoje wejścia będą liczone osobno, by można je odjąć (przełącznik „Bez moich”)."
          className={`ml-auto inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium border transition-all ${isOwner ? 'bg-amber-500/15 border-amber-500/40 text-amber-400' : 'bg-[#0B0F0E] border-white/5 text-[#9CA3AF] hover:text-white'}`}>
          {isOwner ? <UserCheck size={15} /> : <UserX size={15} />}
          {isOwner ? 'To moja przeglądarka ✓' : 'To moja przeglądarka'}
        </button>
      </div>
      <p className="text-[#6B7280] text-xs mb-5">
        {excludeMe
          ? 'Pokazuję liczby bez Twoich wejść (z przeglądarek oznaczonych jako Twoje).'
          : 'Pokazuję wszystkie wejścia, łącznie z Twoimi.'}
      </p>

      {/* Period selector */}
      <div className="flex gap-2 mb-6 flex-wrap items-center">
        <span className="text-[#6B7280] text-sm mr-1">Okres:</span>
        {PERIODS.map(p => (
          <button key={p.value} onClick={() => pickPeriod(p.value)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${period === p.value && !rangeActive ? 'bg-[#10b981] text-white' : 'bg-[#0B0F0E] text-[#9CA3AF] hover:text-white border border-white/5'}`}>
            {p.label}
          </button>
        ))}
        <button onClick={fetchData} className="ml-auto px-3 py-1.5 text-[#6B7280] hover:text-white transition-colors">
          <RefreshCw size={14} />
        </button>
      </div>

      {/* Month + year calendar (past & future) — same as Analityka */}
      <div className="glass-card p-3 mb-6 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
        <div className="flex items-center gap-2">
          <span className="text-[#6B7280] text-xs">Miesiąc:</span>
          <select value={selMonth} onChange={e => applyYM(e.target.value, selYear || String(new Date().getFullYear()))}
            className="bg-[#0B0F0E] border border-white/10 rounded-lg px-2 py-1.5 text-white text-xs">
            <option value="">— miesiąc —</option>
            {MONTHS_PL.map((name, i) => <option key={i} value={i + 1}>{name}</option>)}
          </select>
          <select value={selYear} onChange={e => applyYM(selMonth, e.target.value)}
            className="bg-[#0B0F0E] border border-white/10 rounded-lg px-2 py-1.5 text-white text-xs">
            <option value="">— rok —</option>
            {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[#6B7280] text-xs">Zakres:</span>
          <input type="date" value={rangeStart} onChange={e => { setRangeStart(e.target.value); setSelMonth(''); setSelYear(''); }}
            className="bg-[#0B0F0E] border border-white/10 rounded-lg px-2 py-1.5 text-white text-xs" />
          <span className="text-[#6B7280]">–</span>
          <input type="date" value={rangeEnd} onChange={e => { setRangeEnd(e.target.value); setSelMonth(''); setSelYear(''); }}
            className="bg-[#0B0F0E] border border-white/10 rounded-lg px-2 py-1.5 text-white text-xs" />
        </div>
        {rangeActive ? (
          <div className="flex items-center gap-3">
            <span className="text-[#10b981] text-xs font-medium">Okres: {rangeStart} → {rangeEnd}</span>
            <button onClick={clearRange} className="text-[#6B7280] hover:text-white text-xs underline">Wyczyść</button>
          </div>
        ) : (
          <span className="text-[#6B7280] text-xs ml-auto">Aktualnie: {PERIODS.find(p => p.value === period)?.label}</span>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><div className="spinner"></div></div>
      ) : !data ? (
        <div className="glass-card p-12 text-center text-[#6B7280]">Nie udało się załadować danych</div>
      ) : (
        <div className="space-y-6">
          {/* Key numbers */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              label={`${M.label} (okres)`}
              value={nf(total)}
              color={M.color}
              icon={metric === 'views' ? Eye : Users}
              sub={change == null ? <span className="text-[#6B7280]">brak danych do porównania</span> : (
                <span className={change >= 0 ? 'text-[#10b981] flex items-center gap-1' : 'text-red-400 flex items-center gap-1'}>
                  {change >= 0 ? <ArrowUp size={11} /> : <ArrowDown size={11} />}
                  {Math.abs(change)}% vs poprzedni okres
                </span>
              )}
            />
            <StatCard
              label={METRICS[other].label}
              value={nf(otherTotal)}
              color={METRICS[other].color}
              icon={other === 'views' ? Eye : Users}
            />
            <StatCard label="Średnio / dzień" value={nf(avg)} icon={Activity} color="#f59e0b" />
            <StatCard
              label="Dziś"
              value={nf(todayVal)}
              color={M.color}
              icon={metric === 'views' ? Eye : Users}
              sub={<span className="text-[#6B7280]">{nf(todayOther)} {metric === 'views' ? 'unikalnych' : 'wizyt'}</span>}
            />
          </div>

          {/* Best day */}
          {best.date && best.value > 0 && (
            <div className="glass-card p-4 flex items-center gap-3">
              <Star size={18} className="text-[#f59e0b]" />
              <span className="text-[#9CA3AF] text-sm">
                Najlepszy dzień w okresie: <strong className="text-white">{fmtDay(best.date)}</strong> — <strong style={{ color: M.color }}>{nf(best.value)}</strong> {metric === 'views' ? 'wizyt' : 'unikalnych'}
              </span>
            </div>
          )}

          {/* Chart */}
          <div className="glass-card p-6">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <h3 className="text-white font-semibold">{M.label} — dziennie {excludeMe ? '(bez moich)' : ''}</h3>
              <span className="flex items-center gap-1.5 text-xs text-[#9CA3AF]">
                <span className="w-3 h-3 rounded-sm" style={{ background: M.color }} /> {M.short}
              </span>
            </div>
            {total === 0 ? (
              <p className="text-[#6B7280] text-sm py-12 text-center">Brak odwiedzin w wybranym okresie</p>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
                  <XAxis dataKey="date" tick={{ fill: '#6B7280', fontSize: 11 }} tickFormatter={d => d.slice(5)} minTickGap={16} />
                  <YAxis tick={{ fill: '#6B7280', fontSize: 11 }} width={40} allowDecimals={false} />
                  <Tooltip content={<ChartTooltip />} cursor={{ fill: '#ffffff08' }} />
                  <Bar dataKey="value" fill={M.color} radius={[4, 4, 0, 0]} name={M.short} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Day-by-day stepper */}
          <div className="glass-card p-6">
            <h3 className="text-white font-semibold mb-4">Przeglądaj po dniach {excludeMe ? '(bez moich)' : ''}</h3>
            <div className="flex items-center justify-center gap-6">
              <button onClick={() => setSelectedDay(shiftDay(selectedDay, -1))}
                className="w-10 h-10 rounded-xl bg-[#0B0F0E] border border-white/5 text-[#9CA3AF] hover:text-white hover:border-[#10b981]/40 flex items-center justify-center transition-all">
                <ChevronLeft size={20} />
              </button>

              <div className="text-center min-w-[200px]">
                <p className="text-[#6B7280] text-xs mb-1">{fmtDay(selectedDay)}{isToday && ' (dziś)'}</p>
                <p className="text-3xl font-bold" style={{ color: M.color }}>{nf(dayVal)}</p>
                <p className="text-[#6B7280] text-xs mt-1">
                  {M.short.toLowerCase()} · {nf(dayOther)} {metric === 'views' ? 'unikalnych' : 'wizyt'}
                </p>
              </div>

              <button onClick={() => setSelectedDay(shiftDay(selectedDay, 1))}
                disabled={isToday}
                className={`w-10 h-10 rounded-xl bg-[#0B0F0E] border border-white/5 flex items-center justify-center transition-all ${isToday ? 'opacity-30 cursor-not-allowed text-[#6B7280]' : 'text-[#9CA3AF] hover:text-white hover:border-[#10b981]/40'}`}>
                <ChevronRight size={20} />
              </button>
            </div>
            <div className="flex justify-center mt-4">
              <button onClick={() => setSelectedDay(todayStr())}
                className="text-xs text-[#10b981] hover:text-[#34d399] transition-colors">
                ← Wróć do dziś
              </button>
            </div>
          </div>

          <p className="text-[#6B7280] text-xs text-center leading-relaxed">
            <strong>Wszystkie wizyty</strong> = każde wejście (nawet kilka razy z tego samego adresu). <strong>Unikalne</strong> = różne urządzenia dziennie.<br />
            <strong>To moja przeglądarka</strong> oznacza to urządzenie — Twoje wejścia są liczone osobno, a przełącznik <strong>Bez moich / Z moimi</strong> pokazuje liczby bez nich lub z nimi (działa też wstecz dla wejść już oznaczonych jako Twoje). Licznik anonimowy, bez e-maili i usług zewnętrznych; boty są odfiltrowywane.
          </p>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminVisits;
